import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import CompanyAdminSidebar from '@/components/CompanyAdminSidebar';
import GettingStartedWizard from '@/components/GettingStartedWizard';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Label } from '@/components/ui/label';
import { 
  Users, 
  Clock, 
  Coffee, 
  UserCheck, 
  UserX,
  RefreshCw,
  MapPin,
  BarChart3,
  UtensilsCrossed,
  CalendarDays,
  AlertTriangle,
  TrendingUp,
  CheckCircle,
  XCircle,
  Bell,
  AlertCircle,
  X,
  WifiOff
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { format, formatDistanceToNow, subDays } from 'date-fns';
import { cn } from '@/lib/utils';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { EmployeeBreakStats, EmployeePunctuality, EmployeePeriodProgress } from '@/components/EmployeeAnalytics';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CompanyAdminDashboard = () => {
  const [liveStatus, setLiveStatus] = useState([]);
  const [dashboardStats, setDashboardStats] = useState(null);
  const [lateEmployees, setLateEmployees] = useState([]);
  const [dismissedLateAlerts, setDismissedLateAlerts] = useState(() => {
    // Load dismissed alerts from sessionStorage (persists until browser closes)
    const saved = sessionStorage.getItem('dismissedLateAlerts');
    return saved ? JSON.parse(saved) : [];
  });
  const [lateCheckLoading, setLateCheckLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [showWizard, setShowWizard] = useState(false);
  const [wizardDismissed, setWizardDismissed] = useState(false);
  
  // Date range for charts
  const [dateRange, setDateRange] = useState({
    from: subDays(new Date(), 13), // Default to current fortnight
    to: new Date()
  });

  // Filter out dismissed late employees
  const visibleLateEmployees = lateEmployees.filter(
    emp => !dismissedLateAlerts.includes(emp.employee_id)
  );

  const dismissLateAlert = (employeeId) => {
    const newDismissed = [...dismissedLateAlerts, employeeId];
    setDismissedLateAlerts(newDismissed);
    sessionStorage.setItem('dismissedLateAlerts', JSON.stringify(newDismissed));
  };

  const dismissAllLateAlerts = () => {
    const allIds = lateEmployees.map(emp => emp.employee_id);
    const newDismissed = [...new Set([...dismissedLateAlerts, ...allIds])];
    setDismissedLateAlerts(newDismissed);
    sessionStorage.setItem('dismissedLateAlerts', JSON.stringify(newDismissed));
  };

  const fetchLateClockIns = useCallback(async () => {
    setLateCheckLoading(true);
    try {
      const response = await axios.post(`${API}/admin/check-late-clockins`);
      setLateEmployees(response.data.late_employees || []);
    } catch (error) {
      console.error('Failed to check late clock-ins:', error);
    } finally {
      setLateCheckLoading(false);
    }
  }, []);

  const fetchLiveStatus = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/status/live`);
      setLiveStatus(response.data);
      setLastRefresh(new Date());
    } catch (error) {
      console.error('Failed to fetch live status:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchDashboardStats = useCallback(async () => {
    if (!dateRange.from || !dateRange.to) return;
    
    setStatsLoading(true);
    try {
      const params = new URLSearchParams({
        start_date: dateRange.from.toISOString(),
        end_date: dateRange.to.toISOString()
      });
      const response = await axios.get(`${API}/admin/dashboard-stats?${params}`);
      setDashboardStats(response.data);
    } catch (error) {
      console.error('Failed to fetch dashboard stats:', error);
    } finally {
      setStatsLoading(false);
    }
  }, [dateRange]);

  useEffect(() => {
    fetchLiveStatus();
    const interval = setInterval(fetchLiveStatus, 30000);
    return () => clearInterval(interval);
  }, [fetchLiveStatus]);

  useEffect(() => {
    fetchDashboardStats();
  }, [fetchDashboardStats]);

  // Check wizard status on load
  useEffect(() => {
    const checkWizardStatus = async () => {
      try {
        const response = await axios.get(`${API}/settings/wizard-status`);
        setShowWizard(response.data.show_wizard);
      } catch (error) {
        console.error('Failed to check wizard status:', error);
        setShowWizard(false);
      }
    };
    checkWizardStatus();
  }, []);

  // Check for late clock-ins on dashboard load and every 5 minutes
  useEffect(() => {
    fetchLateClockIns();
    const interval = setInterval(fetchLateClockIns, 300000); // Check every 5 minutes
    return () => clearInterval(interval);
  }, [fetchLateClockIns]);

  const stats = {
    total: liveStatus.length,
    clockedIn: liveStatus.filter(e => e.status === 'clocked_in').length,
    onBreak: liveStatus.filter(e => e.status === 'on_break').length,
    clockedOut: liveStatus.filter(e => e.status === 'clocked_out').length,
  };

  const getStatusBadge = (status, breakType) => {
    switch (status) {
      case 'clocked_in':
        return <Badge className="bg-[hsl(var(--success))] text-white">Clocked In</Badge>;
      case 'on_break':
        return <Badge className="bg-[hsl(var(--warning))] text-white capitalize">{breakType} Break</Badge>;
      case 'clocked_out':
        return <Badge variant="secondary">Clocked Out</Badge>;
      default:
        return null;
    }
  };

  const getStatusDot = (status) => {
    switch (status) {
      case 'clocked_in':
        return 'active';
      case 'on_break':
        return 'break';
      default:
        return 'inactive';
    }
  };

  // Custom tooltip for net hours chart
  const NetHoursTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 shadow-lg rounded-lg border">
          <p className="font-medium text-slate-800">{label}</p>
          <p className="text-sm text-[hsl(243,65%,20%)]">
            Net Hours: <span className="font-mono font-bold">{payload[0].value}h</span>
          </p>
        </div>
      );
    }
    return null;
  };

  // Custom tooltip for breaks chart
  const BreaksTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 shadow-lg rounded-lg border">
          <p className="font-medium text-slate-800">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} className="text-sm" style={{ color: entry.color }}>
              {entry.name}: <span className="font-mono font-bold">{entry.value} min</span>
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="flex flex-col md:flex-row min-h-screen bg-slate-50 w-full overflow-x-hidden">
      <CompanyAdminSidebar />
      
      <main className="flex-1 min-w-0 p-3 sm:p-4 md:p-8 pb-20 md:pb-8 overflow-x-hidden w-full">
        <div className="max-w-7xl mx-auto space-y-4 sm:space-y-6 w-full">
          {/* Getting Started Wizard */}
          {showWizard && !wizardDismissed && (
            <GettingStartedWizard 
              onDismiss={() => setWizardDismissed(true)}
              onComplete={() => setShowWizard(false)}
            />
          )}

          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
            <div>
              <h1 className="text-xl sm:text-2xl md:text-3xl font-bold font-['Manrope'] tracking-tight">Dashboard</h1>
              <p className="text-sm sm:text-base text-slate-500">Real-time employee status overview</p>
            </div>
            <Button 
              variant="outline" 
              onClick={() => { fetchLiveStatus(); fetchDashboardStats(); fetchLateClockIns(); }}
              className="gap-2 self-start sm:self-auto"
              size="sm"
              data-testid="refresh-status-btn"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              <span className="hidden sm:inline">Refresh</span>
              <span className="sm:hidden">Refresh</span>
            </Button>
          </div>

          {/* Late Clock-in Alert Banner */}
          {visibleLateEmployees.length > 0 && (
            <Card className="border-orange-300 bg-orange-50 shadow-md" data-testid="late-clockin-alert">
              <CardContent className="p-3 sm:p-4">
                <div className="flex items-start gap-2 sm:gap-3">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-orange-100 flex items-center justify-center shrink-0">
                    <AlertCircle className="w-4 h-4 sm:w-5 sm:h-5 text-orange-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 sm:gap-2">
                      <h3 className="font-semibold text-orange-800 flex items-center gap-1 sm:gap-2 flex-wrap text-sm sm:text-base">
                        <Bell className="w-3 h-3 sm:w-4 sm:h-4" />
                        <span>Late Clock-in Alert</span>
                        <Badge variant="outline" className="bg-orange-100 text-orange-700 border-orange-300 text-xs">
                          {visibleLateEmployees.length}
                        </Badge>
                      </h3>
                      <div className="flex items-center gap-1 self-end sm:self-auto">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={fetchLateClockIns}
                          disabled={lateCheckLoading}
                          className="text-orange-600 hover:bg-orange-100 p-1 h-auto"
                          title="Refresh"
                        >
                          <RefreshCw className={`w-4 h-4 ${lateCheckLoading ? 'animate-spin' : ''}`} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={dismissAllLateAlerts}
                          className="text-orange-600 hover:bg-orange-100 p-1 h-auto text-xs"
                          title="Dismiss all"
                        >
                          Dismiss All
                        </Button>
                      </div>
                    </div>
                    <p className="text-xs sm:text-sm text-orange-700 mt-1 mb-2 sm:mb-3">
                      Rostered employees not clocked in on time:
                    </p>
                    <div className="space-y-2">
                      {visibleLateEmployees.map((emp, idx) => (
                        <div 
                          key={emp.employee_id || idx} 
                          className="flex flex-col sm:flex-row sm:items-center justify-between bg-white rounded-lg p-2 sm:p-3 border border-orange-200 gap-2"
                          data-testid={`late-employee-${idx}`}
                        >
                          <div className="flex items-center gap-2 sm:gap-3">
                            <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-orange-100 flex items-center justify-center text-orange-700 font-semibold text-xs sm:text-sm shrink-0">
                              {emp.employee_name?.charAt(0)?.toUpperCase() || '?'}
                            </div>
                            <div className="min-w-0">
                              <p className="font-medium text-slate-800 text-sm truncate">{emp.employee_name}</p>
                              <p className="text-xs text-slate-500">
                                Scheduled: {emp.scheduled_start}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 justify-between sm:justify-end pl-9 sm:pl-0">
                            <Badge 
                              variant="outline" 
                              className={`text-xs ${emp.has_clocked_in 
                                ? "bg-yellow-100 text-yellow-700 border-yellow-300" 
                                : "bg-red-100 text-red-700 border-red-300"
                              }`}
                            >
                              {emp.has_clocked_in ? 'Late' : 'Not in'}
                            </Badge>
                            <p className="text-xs text-orange-600 font-medium">
                              +{emp.minutes_late}m
                            </p>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => dismissLateAlert(emp.employee_id)}
                              className="p-1 h-6 w-6 text-slate-400 hover:text-slate-600 hover:bg-slate-100"
                              title="Dismiss"
                            >
                              <X className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Stats Grid */}
          <div className="grid grid-cols-2 gap-3 sm:gap-4 md:grid-cols-4">
            <Card className="stat-card card-hover p-3 sm:p-6" data-testid="stat-total">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-[hsl(243,65%,20%)]/10 flex items-center justify-center shrink-0">
                  <Users className="w-4 h-4 sm:w-5 sm:h-5 text-[hsl(243,65%,20%)]" />
                </div>
                <div className="min-w-0">
                  <p className="stat-value text-xl sm:text-3xl">{stats.total}</p>
                  <p className="stat-label text-xs">Total</p>
                </div>
              </div>
            </Card>
            
            <Card className="stat-card card-hover p-3 sm:p-6" data-testid="stat-clocked-in">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-[hsl(var(--success-bg))] flex items-center justify-center shrink-0">
                  <UserCheck className="w-4 h-4 sm:w-5 sm:h-5 text-[hsl(var(--success))]" />
                </div>
                <div className="min-w-0">
                  <p className="stat-value text-xl sm:text-3xl text-[hsl(var(--success))]">{stats.clockedIn}</p>
                  <p className="stat-label text-xs">Active</p>
                </div>
              </div>
            </Card>
            
            <Card className="stat-card card-hover p-3 sm:p-6" data-testid="stat-on-break">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-[hsl(var(--warning-bg))] flex items-center justify-center shrink-0">
                  <Coffee className="w-4 h-4 sm:w-5 sm:h-5 text-[hsl(var(--warning))]" />
                </div>
                <div className="min-w-0">
                  <p className="stat-value text-xl sm:text-3xl text-[hsl(var(--warning))]">{stats.onBreak}</p>
                  <p className="stat-label text-xs">On Break</p>
                </div>
              </div>
            </Card>
            
            <Card className="stat-card card-hover p-3 sm:p-6" data-testid="stat-clocked-out">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-slate-100 flex items-center justify-center shrink-0">
                  <UserX className="w-4 h-4 sm:w-5 sm:h-5 text-slate-400" />
                </div>
                <div className="min-w-0">
                  <p className="stat-value text-xl sm:text-3xl text-slate-400">{stats.clockedOut}</p>
                  <p className="stat-label text-xs">Offline</p>
                </div>
              </div>
            </Card>
          </div>

          {/* Charts Section */}
          {/* Date Range Selector */}
          <Card className="border-0 shadow-lg">
            <CardContent className="pt-4 sm:pt-6">
              <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-end gap-3 sm:gap-4">
                <div className="grid grid-cols-2 sm:flex gap-3 sm:gap-4">
                  <div className="space-y-2">
                    <Label className="text-xs sm:text-sm font-medium">Start Date</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className={cn(
                            "w-full sm:w-[160px] justify-start text-left font-normal text-xs sm:text-sm px-2 sm:px-3",
                            !dateRange.from && "text-muted-foreground"
                          )}
                          data-testid="chart-start-date"
                        >
                          <CalendarDays className="mr-1 sm:mr-2 h-3 w-3 sm:h-4 sm:w-4 shrink-0" />
                          <span className="truncate">{dateRange.from ? format(dateRange.from, "dd/MM/yyyy") : "Pick a date"}</span>
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={dateRange.from}
                          onSelect={(date) => setDateRange(prev => ({ ...prev, from: date }))}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-xs sm:text-sm font-medium">End Date</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className={cn(
                            "w-full sm:w-[160px] justify-start text-left font-normal text-xs sm:text-sm px-2 sm:px-3",
                            !dateRange.to && "text-muted-foreground"
                          )}
                          data-testid="chart-end-date"
                        >
                          <CalendarDays className="mr-1 sm:mr-2 h-3 w-3 sm:h-4 sm:w-4 shrink-0" />
                          <span className="truncate">{dateRange.to ? format(dateRange.to, "dd/MM/yyyy") : "Pick a date"}</span>
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={dateRange.to}
                          onSelect={(date) => setDateRange(prev => ({ ...prev, to: date }))}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>

                {/* Quick presets */}
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-xs px-2 sm:px-3"
                    onClick={() => setDateRange({ from: subDays(new Date(), 6), to: new Date() })}
                    data-testid="preset-week"
                  >
                    7 Days
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-xs px-2 sm:px-3"
                    onClick={() => setDateRange({ from: subDays(new Date(), 13), to: new Date() })}
                    data-testid="preset-fortnight"
                  >
                    14 Days
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-xs px-2 sm:px-3"
                    onClick={() => setDateRange({ from: subDays(new Date(), 29), to: new Date() })}
                    data-testid="preset-month"
                  >
                    30 Days
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
            {/* Net Hours Chart */}
            <Card className="border-0 shadow-lg" data-testid="net-hours-chart">
              <CardHeader className="p-4 sm:p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="font-['Manrope'] flex items-center gap-2 text-base sm:text-lg">
                      <BarChart3 className="w-4 h-4 sm:w-5 sm:h-5" />
                      Net Hours Worked
                    </CardTitle>
                    <CardDescription className="flex items-center gap-1 mt-1 text-xs sm:text-sm">
                      <CalendarDays className="w-3 h-3" />
                      {dashboardStats?.dateRange?.label || 'Selected Period'}
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-2 sm:p-6 pt-0">
                {statsLoading ? (
                  <div className="flex justify-center py-12">
                    <div className="spinner" />
                  </div>
                ) : !dashboardStats?.netHoursData?.length ? (
                  <div className="empty-state py-8 sm:py-12">
                    <BarChart3 className="empty-state-icon w-12 h-12 sm:w-16 sm:h-16" />
                    <p className="text-slate-500 text-sm">No employees on roster</p>
                    <p className="text-xs sm:text-sm text-slate-400">Add employees to see their hours</p>
                  </div>
                ) : (
                  <div className="h-[250px] sm:h-[300px] w-full">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={dashboardStats.netHoursData}
                        margin={{ top: 10, right: 10, left: 0, bottom: 20 }}
                        barCategoryGap="15%"
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                        <XAxis 
                          dataKey="name" 
                          tick={{ fontSize: 10, fill: '#64748b' }}
                          tickLine={false}
                          axisLine={{ stroke: '#e2e8f0' }}
                          angle={-45}
                          textAnchor="end"
                          height={60}
                          interval={0}
                        />
                        <YAxis 
                          tick={{ fontSize: 11, fill: '#64748b' }}
                          tickLine={false}
                          axisLine={{ stroke: '#e2e8f0' }}
                          tickFormatter={(value) => `${value}h`}
                          domain={[0, 'dataMax + 5']}
                          width={45}
                          allowDecimals={false}
                        />
                        <Tooltip content={<NetHoursTooltip />} />
                        <Bar 
                          dataKey="netHours" 
                          name="Net Hours"
                          fill="hsl(243, 65%, 20%)" 
                          radius={[4, 4, 0, 0]}
                          label={{ position: 'top', fontSize: 10, fill: '#64748b', formatter: (value) => `${value}h` }}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Break Time by Employee - REPLACED OLD CHART */}
            <EmployeeBreakStats days={Math.ceil((dateRange.to - dateRange.from) / (1000 * 60 * 60 * 24))} />
          </div>

          {/* Employee Analytics Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
            {/* Punctuality by Employee */}
            <EmployeePunctuality days={Math.ceil((dateRange.to - dateRange.from) / (1000 * 60 * 60 * 24))} />

            {/* Period Progress by Employee */}
            <EmployeePeriodProgress />
          </div>

          {/* Overtime Alerts */}
          {dashboardStats?.overtimeAlerts?.length > 0 && (
            <Card className="border-0 shadow-lg border-l-4 border-l-amber-500" data-testid="overtime-alerts">
              <CardHeader className="p-4 sm:p-6">
                <CardTitle className="font-['Manrope'] flex items-center gap-2 text-amber-600 text-base sm:text-lg">
                  <AlertTriangle className="w-4 h-4 sm:w-5 sm:h-5" />
                  Overtime Alerts
                </CardTitle>
                <CardDescription className="text-xs sm:text-sm">
                  Employees exceeding {dashboardStats.standardFortnightHours} hours (standard fortnight hours)
                </CardDescription>
              </CardHeader>
              <CardContent className="p-4 sm:p-6 pt-0">
                <div className="space-y-2 sm:space-y-3">
                  {dashboardStats.overtimeAlerts.map((alert, idx) => (
                    <div key={idx} className="flex flex-col sm:flex-row sm:items-center justify-between p-2 sm:p-3 bg-amber-50 rounded-lg border border-amber-200 gap-2">
                      <div className="flex items-center gap-2 sm:gap-3">
                        <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-amber-100 flex items-center justify-center text-amber-700 font-semibold text-xs sm:text-sm shrink-0">
                          {alert.name.charAt(0).toUpperCase()}
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium text-amber-900 text-sm truncate">{alert.name}</p>
                          <p className="text-xs text-amber-600">
                            {alert.net_hours}h worked (Std: {alert.standard_hours}h)
                          </p>
                        </div>
                      </div>
                      <Badge className="bg-amber-100 text-amber-700 font-semibold self-end sm:self-auto text-xs">
                        +{alert.overtime_hours}h overtime
                      </Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Attendance & Late Starts Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
            {/* Attendance Rate */}
            <Card className="border-0 shadow-lg" data-testid="attendance-chart">
              <CardHeader className="p-4 sm:p-6">
                <CardTitle className="font-['Manrope'] flex items-center gap-2 text-base sm:text-lg">
                  <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" />
                  Attendance Rate
                </CardTitle>
                <CardDescription className="text-xs sm:text-sm">
                  Days worked vs. scheduled in the selected period
                </CardDescription>
              </CardHeader>
              <CardContent className="p-4 sm:p-6 pt-0">
                {statsLoading ? (
                  <div className="flex justify-center py-8">
                    <div className="spinner" />
                  </div>
                ) : !dashboardStats?.attendanceData?.length ? (
                  <div className="empty-state py-8">
                    <CheckCircle className="empty-state-icon w-12 h-12" />
                    <p className="text-slate-500 text-sm">No attendance data</p>
                    <p className="text-xs text-slate-400">Add shifts to roster to track attendance</p>
                  </div>
                ) : (
                  <div className="space-y-2 sm:space-y-3 max-h-[250px] overflow-y-auto">
                    {dashboardStats.attendanceData.map((emp, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2 sm:p-3 bg-slate-50 rounded-lg gap-2">
                        <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                          <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-slate-200 flex items-center justify-center text-slate-700 font-semibold text-xs sm:text-sm shrink-0">
                            {emp.name.charAt(0).toUpperCase()}
                          </div>
                          <div className="min-w-0">
                            <p className="font-medium text-xs sm:text-sm truncate">{emp.name}</p>
                            <p className="text-xs text-slate-500">
                              {emp.worked}/{emp.scheduled} days
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <div className="w-16 sm:w-24 h-2 bg-slate-200 rounded-full overflow-hidden">
                            <div 
                              className={`h-full rounded-full ${emp.rate >= 90 ? 'bg-green-500' : emp.rate >= 70 ? 'bg-amber-500' : 'bg-red-500'}`}
                              style={{ width: `${Math.min(emp.rate, 100)}%` }}
                            />
                          </div>
                          <span className={`text-xs sm:text-sm font-semibold ${emp.rate >= 90 ? 'text-green-600' : emp.rate >= 70 ? 'text-amber-600' : 'text-red-600'}`}>
                            {emp.rate}%
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Late Starts / Punctuality */}
            <Card className="border-0 shadow-lg" data-testid="late-starts-chart">
              <CardHeader className="p-4 sm:p-6">
                <CardTitle className="font-['Manrope'] flex items-center gap-2 text-base sm:text-lg">
                  <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
                  Punctuality
                </CardTitle>
                <CardDescription className="text-xs sm:text-sm">
                  On-time arrivals vs. late starts
                </CardDescription>
              </CardHeader>
              <CardContent className="p-4 sm:p-6 pt-0">
                {statsLoading ? (
                  <div className="flex justify-center py-8">
                    <div className="spinner" />
                  </div>
                ) : !dashboardStats?.punctualityData?.length ? (
                  <div className="empty-state py-8">
                    <Clock className="empty-state-icon w-12 h-12" />
                    <p className="text-slate-500 text-sm">No punctuality data</p>
                    <p className="text-xs text-slate-400">Add shifts with start times to roster</p>
                  </div>
                ) : (
                  <div className="space-y-2 sm:space-y-3 max-h-[250px] overflow-y-auto">
                    {dashboardStats.punctualityData.map((emp, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2 sm:p-3 bg-slate-50 rounded-lg gap-2">
                        <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                          <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-slate-200 flex items-center justify-center text-slate-700 font-semibold text-xs sm:text-sm shrink-0">
                            {emp.name.charAt(0).toUpperCase()}
                          </div>
                          <div className="min-w-0">
                            <p className="font-medium text-xs sm:text-sm truncate">{emp.name}</p>
                            <p className="text-xs text-slate-500">
                              {emp.late > 0 ? (
                                <span className="text-amber-600">
                                  {emp.late} late, {emp.on_time} on time
                                </span>
                              ) : (
                                <span className="text-green-600">On time ({emp.on_time})</span>
                              )}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-1 sm:gap-2 shrink-0">
                          {emp.rate >= 90 ? (
                            <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 text-green-500" />
                          ) : emp.rate >= 70 ? (
                            <Clock className="w-3 h-3 sm:w-4 sm:h-4 text-amber-500" />
                          ) : (
                            <XCircle className="w-3 h-3 sm:w-4 sm:h-4 text-red-500" />
                          )}
                          <span className={`text-xs sm:text-sm font-semibold ${emp.rate >= 90 ? 'text-green-600' : emp.rate >= 70 ? 'text-amber-600' : 'text-red-600'}`}>
                            {emp.rate}%
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Employee Status List */}
          <Card className="border-0 shadow-lg">
            <CardHeader className="border-b p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                <CardTitle className="font-['Manrope'] flex items-center gap-2 text-base sm:text-lg">
                  <Clock className="w-4 h-4 sm:w-5 sm:h-5" />
                  Live Status
                </CardTitle>
                <p className="text-xs sm:text-sm text-slate-500">
                  Updated {formatDistanceToNow(lastRefresh, { addSuffix: true })}
                </p>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {loading ? (
                <div className="flex justify-center py-12">
                  <div className="spinner" />
                </div>
              ) : liveStatus.length === 0 ? (
                <div className="empty-state py-12">
                  <Users className="empty-state-icon w-12 h-12 sm:w-16 sm:h-16" />
                  <p className="text-slate-500 text-sm">No employees found</p>
                  <p className="text-xs sm:text-sm text-slate-400">Invite employees to see their status</p>
                </div>
              ) : (
                <ScrollArea className="h-[300px]">
                  <div className="divide-y">
                    {liveStatus.map((employee) => (
                      <div 
                        key={employee.id} 
                        className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-4 px-3 sm:px-6 py-3 sm:py-4 hover:bg-slate-50 transition-colors"
                        data-testid={`employee-status-${employee.id}`}
                      >
                        <div className="flex items-center gap-3">
                          <div className="relative shrink-0">
                            <div className="employee-avatar w-8 h-8 sm:w-10 sm:h-10 text-sm">
                              {employee.name.charAt(0).toUpperCase()}
                            </div>
                            <div className={`status-dot absolute -bottom-0.5 -right-0.5 ${getStatusDot(employee.status)}`} />
                          </div>
                          <div className="min-w-0">
                            <p className="font-medium text-sm truncate">{employee.name}</p>
                            <p className="text-xs text-slate-500 truncate">{employee.email}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 sm:gap-4 flex-wrap pl-11 sm:pl-0">
                          {/* Offline Badge - Show prominently for managers */}
                          {employee.is_offline && (
                            <Badge className="bg-amber-100 text-amber-700 border-amber-300" data-testid="offline-badge">
                              <WifiOff className="w-3 h-3 mr-1" />
                              Offline Entry
                            </Badge>
                          )}
                          {employee.office_name && !employee.is_offline && (
                            <p className="text-xs text-slate-500 flex items-center gap-1">
                              <MapPin className="w-3 h-3" />
                              <span className="truncate max-w-[80px] sm:max-w-none">{employee.office_name}</span>
                            </p>
                          )}
                          {employee.is_offsite && !employee.is_offline && (
                            <Badge variant="outline" className="text-orange-600 border-orange-300 text-xs">
                              Off-site
                            </Badge>
                          )}
                          {employee.status === 'clocked_in' && employee.clock_in_time && (
                            <p className="text-xs text-slate-500 font-mono">
                              Since {new Date(employee.clock_in_time).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                            </p>
                          )}
                          {employee.status === 'on_break' && employee.break_start && (
                            <p className="text-xs text-slate-500 font-mono">
                              {formatDistanceToNow(new Date(employee.break_start))}
                            </p>
                          )}
                          {getStatusBadge(employee.status, employee.break_type)}
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default CompanyAdminDashboard;
